import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import { isAxiosError } from "axios";
import type {
  ContactCommunicationThreadsPage,
  ContactEventsResponse,
} from "@/types/contactActivity.ts";
import type { ContactProfileResponse } from "@/types/contactProfile.ts";
import type { MemoryResp } from "@/types/memory.ts";

const COMMUNICATIONS_PAGE_SIZE = 20;

/**
 * Query factories for the contact sidebar's activity tabs (BIG-5337): the
 * communications and calendar events a contact participates in.
 */
export const contactActivityQueries = {
  communications: (contactId: string) =>
    infiniteQueryOptions({
      queryKey: ["contactActivity", contactId, "communications"] as const,
      queryFn: async ({ pageParam, signal }) => {
        const params = new URLSearchParams({ limit: String(COMMUNICATIONS_PAGE_SIZE) });
        if (pageParam) params.set("cursor", pageParam);
        const response = await api.get<ContactCommunicationThreadsPage>(
          `/api/a/contacts/${contactId}/communications?${params}`,
          { signal },
        );
        return response.data;
      },
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    }),
  events: (contactId: string) =>
    queryOptions({
      queryKey: ["contactActivity", contactId, "events"] as const,
      queryFn: async ({ signal }) => {
        const response = await api.get<ContactEventsResponse>(
          `/api/a/contacts/${contactId}/events`,
          {
            signal,
          },
        );
        return response.data;
      },
    }),
  memories: (contactId: string) =>
    queryOptions({
      queryKey: ["contactActivity", contactId, "memories"] as const,
      queryFn: async ({ signal }): Promise<MemoryResp[]> => {
        const response = await api.get<MemoryResp[]>(`/api/a/contacts/${contactId}/memories`, {
          signal,
        });
        return response.data;
      },
    }),
  profile: (contactId: string) =>
    queryOptions({
      queryKey: ["contactActivity", contactId, "profile"] as const,
      queryFn: async ({ signal }): Promise<ContactProfileResponse | null> => {
        try {
          const response = await api.get<ContactProfileResponse>(
            `/api/a/contacts/${contactId}/profile`,
            { signal },
          );
          return response.data;
        } catch (err) {
          // A contact with no profile built yet is an expected empty state, not an error.
          if (isAxiosError(err) && err.response?.status === 404) return null;
          throw err;
        }
      },
    }),
};

export const useContactCommunications = (contactId: string) =>
  useInfiniteQuery(contactActivityQueries.communications(contactId));

export const useContactEvents = (contactId: string) =>
  useQuery(contactActivityQueries.events(contactId));

export const useContactProfile = (contactId: string, enabled = true) =>
  useQuery({ ...contactActivityQueries.profile(contactId), enabled });

export const useContactMemories = (contactId: string) =>
  useQuery(contactActivityQueries.memories(contactId));
